import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import { useAuth } from "../AuthContext";

export const NotAuthorized = () => {
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuth();

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    if (isAuthenticated) {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #041E42 0%, #0a3d7a 50%, #041E42 100%)",
        padding: 3,
      }}
    >
      <Card
        sx={{
          maxWidth: 480,
          width: "100%",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        {/* Header accent bar */}
        <Box
          sx={{
            height: 6,
            background: "linear-gradient(90deg, #f44336 0%, #ff7043 100%)",
          }}
        />

        <CardContent sx={{ p: 4, textAlign: "center" }}>
          {/* Icon */}
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              backgroundColor: "rgba(244, 67, 54, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <LockOutlinedIcon
              sx={{
                fontSize: 40,
                color: "#f44336",
              }}
            />
          </Box>

          {/* Title */}
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              color: "#041E42",
              mb: 1,
            }}
          >
            Access Denied
          </Typography>

          {/* Error code */}
          <Typography
            variant="subtitle2"
            sx={{
              color: "#f44336",
              fontWeight: 600,
              letterSpacing: 1,
              mb: 2,
            }}
          >
            ERROR 403
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            sx={{
              color: "#666",
              mb: 4,
              lineHeight: 1.7,
            }}
          >
            You do not have permission to access this page. 
            Please contact your administrator if you believe this is an error.
          </Typography>

          {/* Action buttons */}
          <Stack spacing={2}>
            <Button
              variant="contained"
              startIcon={<HomeIcon />}
              onClick={handleGoHome}
              sx={{
                backgroundColor: "#041E42",
                py: 1.5,
                fontSize: "0.95rem",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#0a3d7a",
                },
              }}
            >
              Go to Dashboard
            </Button>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleGoBack}
                sx={{
                  flex: 1,
                  borderColor: "#041E42",
                  color: "#041E42",
                  py: 1.25,
                  "&:hover": {
                    borderColor: "#0a3d7a",
                    backgroundColor: "rgba(4, 30, 66, 0.04)",
                  },
                }}
              >
                Go Back
              </Button>

              {isAuthenticated && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleLogout}
                  sx={{
                    flex: 1,
                    py: 1.25,
                  }}
                >
                  Sign Out
                </Button>
              )}
            </Stack>
          </Stack>

          {/* Help text */}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 4,
              color: "#999",
            }}
          >
            Need help? Contact IT Support
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
